import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import logger from '../lib/logger';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  ArrowLeft, Save, RotateCcw, Loader2, Clock, Users, Layers,
  Server, Settings, AlertTriangle, History, ChevronDown, ChevronUp, Undo2
} from 'lucide-react';
import { toast } from 'sonner';
import SectionGuide, { resetGuides } from '../components/SectionGuide';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function NumberInput({ label, value, onChange, suffix = '', min = 0, max = 999, step = 1, helpText }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <div className="flex items-center gap-1.5">
        <input type="number" value={value} onChange={e => onChange(parseFloat(e.target.value) || 0)}
          min={min} max={max} step={step}
          className="h-8 w-24 px-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-violet-300 tabular-nums"
        />
        {suffix && <span className="text-xs text-slate-400">{suffix}</span>}
      </div>
      {helpText && <p className="text-[10px] text-slate-400 mt-0.5">{helpText}</p>}
    </div>
  );
}

const PHASE_COLUMNS = [
  'Initiation',
  'Design / Discovery',
  'Execution',
  'Validation (UAT)',
  'Transition - Go-Live, Hypercare',
];

function OverlayPhaseFractionsEditor({ fractions, onChange }) {
  const components = Object.keys(fractions);
  if (components.length === 0) {
    return <p className="text-[11px] text-slate-400 py-2">No overlay components configured.</p>;
  }

  const handleCell = (comp, phase, rawPct) => {
    const pct = Math.max(0, Math.min(100, parseFloat(rawPct) || 0));
    const next = JSON.parse(JSON.stringify(fractions));
    if (!next[comp]) next[comp] = {};
    if (pct === 0) {
      delete next[comp][phase];
    } else {
      next[comp][phase] = parseFloat((pct / 100).toFixed(4));
    }
    onChange(next);
  };

  return (
    <div className="overflow-x-auto" data-testid="overlay-fractions-editor">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-slate-200">
            <th className="text-left py-2 pr-2 font-semibold text-slate-600 w-44">Component</th>
            {PHASE_COLUMNS.map(p => (
              <th key={p} className="text-right px-1.5 py-2 font-medium text-slate-500" title={p}>
                <div className="text-[10px] leading-tight">{p.replace(' - ', ' ')}</div>
              </th>
            ))}
            <th className="text-right pl-2 py-2 font-semibold text-slate-600 w-16">Total</th>
          </tr>
        </thead>
        <tbody>
          {components.map(comp => {
            const row = fractions[comp] || {};
            const total = Object.values(row).reduce((s, v) => s + (v || 0), 0);
            const totalPct = Math.round(total * 100);
            const isValid = Math.abs(total - 1.0) < 0.01;
            return (
              <tr key={comp} className="border-b border-slate-100 last:border-b-0" data-testid={`overlay-row-${comp}`}>
                <td className="py-2 pr-2 text-[#0F2040] font-medium">{comp}</td>
                {PHASE_COLUMNS.map(phase => {
                  const pct = Math.round((row[phase] || 0) * 100);
                  return (
                    <td key={phase} className="px-1 py-1.5">
                      <input
                        type="number"
                        value={pct}
                        onChange={e => handleCell(comp, phase, e.target.value)}
                        min={0} max={100} step={1}
                        className="h-7 w-14 px-1.5 text-xs border rounded tabular-nums text-right focus:outline-none focus:ring-1 focus:ring-violet-300"
                        data-testid={`overlay-cell-${comp}-${phase}`}
                      />
                    </td>
                  );
                })}
                <td className={`text-right pl-2 py-2 font-semibold tabular-nums ${isValid ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {totalPct}%
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="text-[10px] text-slate-400 mt-3">
        Each row must total 100%. Invalid rows will be rejected on save.
      </p>
    </div>
  );
}

export default function PlanningConfigPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [original, setOriginal] = useState(null);
  const [roleLabels, setRoleLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [guideResetKey, setGuideResetKey] = useState(0);
  const [changelog, setChangelog] = useState([]);
  const [changelogTotal, setChangelogTotal] = useState(0);
  const [changelogPage, setChangelogPage] = useState(1);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [fieldLabels, setFieldLabels] = useState({});
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoring, setRestoring] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/planner/planning-config`, { withCredentials: true });
      const { role_labels, ...cfg } = res.data;
      setConfig(cfg);
      setOriginal(JSON.parse(JSON.stringify(cfg)));
      setRoleLabels(role_labels || {});
    } catch (err) {
      toast.error('Failed to load planning configuration');
      logger.error('Planning config load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const fetchChangelog = useCallback(async (pg = 1) => {
    try {
      const res = await axios.get(`${API_URL}/api/planner/config-changelog?page=${pg}&limit=10`, { withCredentials: true });
      setChangelog(res.data.entries || []);
      setChangelogTotal(res.data.total || 0);
      setChangelogPage(pg);
      if (res.data.field_labels) setFieldLabels(res.data.field_labels);
    } catch (err) { logger.error('Failed to load changelog:', err); }
  }, []);

  useEffect(() => { if (changelogOpen) fetchChangelog(1); }, [changelogOpen, fetchChangelog]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/planner/planning-config`, config, { withCredentials: true });
      const { role_labels: rl, ...cfg } = res.data.config;
      setConfig(cfg);
      setOriginal(JSON.parse(JSON.stringify(cfg)));
      if (rl) setRoleLabels(rl);
      toast.success('Planning configuration saved');
      if (changelogOpen) fetchChangelog(1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const resetConfig = () => {
    setConfig(JSON.parse(JSON.stringify(original)));
    toast.info('Changes reverted');
  };

  const handleRestore = async () => {
    if (!restoreTarget) return;
    setRestoring(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/planner/config-changelog/restore`,
        { timestamp: restoreTarget.timestamp },
        { withCredentials: true }
      );
      const { role_labels: rl, ...cfg } = res.data.config;
      setConfig(cfg);
      setOriginal(JSON.parse(JSON.stringify(cfg)));
      if (rl) setRoleLabels(rl);
      toast.success(res.data.message || 'Configuration restored');
      fetchChangelog(1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoring(false);
      setRestoreTarget(null);
    }
  };

  const hasChanges = JSON.stringify(config) !== JSON.stringify(original);

  // Validate overlay_phase_fractions — each component row must sum to 1.0 (±0.01)
  const overlayInvalidRows = (() => {
    const bad = [];
    const opf = (config && config.overlay_phase_fractions) || {};
    for (const [comp, phases] of Object.entries(opf)) {
      if (!phases) continue;
      const total = Object.values(phases).reduce((s, v) => s + (v || 0), 0);
      if (Math.abs(total - 1.0) > 0.01) bad.push(comp);
    }
    return bad;
  })();
  const overlayValid = overlayInvalidRows.length === 0;

  const updateField = (path, value) => {
    setConfig(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let ref = next;
      for (let i = 0; i < keys.length - 1; i++) ref = ref[keys[i]];
      ref[keys[keys.length - 1]] = value;
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
      </div>
    );
  }

  if (!config) return null;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5" data-testid="planning-config-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="text-slate-400 hover:text-slate-600 h-8 w-8 p-0" data-testid="planning-config-back">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-[#0F2040]">Planning Configuration</h1>
            <p className="text-xs text-slate-500 mt-0.5">Adjust baseline hours, timing rules, and planning multipliers</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <Button variant="outline" size="sm" onClick={resetConfig} className="text-xs h-8" data-testid="planning-config-reset">
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Revert
            </Button>
          )}
          <Button onClick={saveConfig} disabled={saving || !hasChanges || !isSuperAdmin || !overlayValid}
            className="bg-[#0F2040] hover:bg-[#172E5A] text-white text-xs h-8"
            data-testid="planning-config-save"
            title={!overlayValid ? `Row totals must equal 100%: ${overlayInvalidRows.join(', ')}` : ''}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
            Save Configuration
          </Button>
        </div>
      </div>

      {!isSuperAdmin && (
        <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span className="text-xs">Only Super Admins can modify planning configuration. You are viewing in read-only mode.</span>
        </div>
      )}

      <SectionGuide id="planning-config-overview" title="Planning Configuration" icon={Settings} resetKey={guideResetKey}>
        <p>
          These settings control how the <strong>Project Planner</strong> calculates timelines and distributes effort. Changes here affect all future timeline generations — existing saved plans are not retroactively recalculated.
        </p>
      </SectionGuide>

      {/* ── Section 1: Complexity Week Presets ── */}
      <Card data-testid="section-week-presets">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-violet-500" />
            <CardTitle className="text-sm font-semibold text-[#0F2040]">Phase Duration Presets</CardTitle>
          </div>
          <p className="text-[11px] text-slate-500 mt-1">Base number of weeks for each complexity tier before dynamic adjustments are applied.</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-6">
            {Object.entries(config.complexity_week_presets).map(([key, val]) => (
              <NumberInput key={key}
                label={`${key.charAt(0).toUpperCase() + key.slice(1)} Complexity`}
                value={val} onChange={v => updateField(`complexity_week_presets.${key}`, Math.round(v))}
                suffix="weeks" min={1} max={52}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ── Section 2: Timing Rules ── */}
      <Card data-testid="section-timing-rules">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Settings className="w-4 h-4 text-amber-500" />
            <CardTitle className="text-sm font-semibold text-[#0F2040]">Dynamic Timing Rules</CardTitle>
          </div>
          <p className="text-[11px] text-slate-500 mt-1">Controls how extra effort hours extend the project timeline beyond the base weeks.</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-6">
            <NumberInput
              label="Effort Increment Threshold"
              value={config.effort_increment_hours} onChange={v => updateField('effort_increment_hours', Math.round(v))}
              suffix="hours" min={1} max={500}
              helpText="Every time effort exceeds the base by this many hours, the timeline extends."
            />
            <NumberInput
              label="Timing Increase per Increment"
              value={Math.round(config.timing_increase_pct * 100)} onChange={v => updateField('timing_increase_pct', v / 100)}
              suffix="%" min={1} max={100} step={1}
              helpText="Percentage the timeline extends for each effort increment."
            />
          </div>

          {/* Hypercare Complete override — when set, forces the last
              milestone to Go-Live + N days regardless of the component's
              computed span. Blank / 0 = use component-driven value. */}
          <div className="mt-5 pt-5 border-t border-dashed border-slate-200"
               data-testid="hypercare-duration-override">
            <NumberInput
              label="Hypercare Complete Override"
              value={config.hypercare_duration_days_override || 0}
              onChange={v => {
                const n = Math.round(v);
                updateField('hypercare_duration_days_override', n > 0 ? n : null);
              }}
              suffix="days post go-live" min={0} max={365} step={1}
              helpText="When set, forces 'Hypercare Complete' to land exactly N days after Go-Live across every SOW. Leave at 0 to derive duration from each estimate's Hypercare component."
            />
          </div>
        </CardContent>
      </Card>

      {/* ── Section 3: Role Baseline Hours ── */}
      <Card data-testid="section-role-hours">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-blue-500" />
            <CardTitle className="text-sm font-semibold text-[#0F2040]">Role Baseline Hours</CardTitle>
          </div>
          <p className="text-[11px] text-slate-500 mt-1">Default effort-hours allocated to each role. These are reference values used in baseline calculations.</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {Object.entries(config.role_baseline_hours).map(([role, hrs]) => (
              <NumberInput key={role}
                label={`${roleLabels[role] || role} (${role})`}
                value={hrs} onChange={v => updateField(`role_baseline_hours.${role}`, Math.round(v))}
                suffix="hrs" min={0} max={2000}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ── Section 4: Complexity Multipliers ── */}
      <Card data-testid="section-complexity-multipliers">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-emerald-500" />
            <CardTitle className="text-sm font-semibold text-[#0F2040]">Complexity Multipliers</CardTitle>
            <Badge className="text-[8px] bg-violet-100 text-violet-700 border-violet-200" variant="outline">Planner Only</Badge>
          </div>
          <p className="text-[11px] text-slate-500 mt-1">Scaling factors applied by the Planner based on the estimate's complexity tier. These are independent of the Estimator's project factors.</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-6">
            {Object.entries(config.complexity_multipliers).map(([key, val]) => (
              <NumberInput key={key}
                label={`${key.charAt(0).toUpperCase() + key.slice(1)}`}
                value={val} onChange={v => updateField(`complexity_multipliers.${key}`, parseFloat(v.toFixed(2)))}
                suffix="x" min={0.1} max={5} step={0.05}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ── Section 5: Implementation Type Multipliers ── */}
      <Card data-testid="section-impl-multipliers">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-teal-500" />
            <CardTitle className="text-sm font-semibold text-[#0F2040]">Implementation Type Multipliers</CardTitle>
            <Badge className="text-[8px] bg-violet-100 text-violet-700 border-violet-200" variant="outline">Planner Only</Badge>
          </div>
          <p className="text-[11px] text-slate-500 mt-1">Scaling factors applied by the Planner based on implementation type. Independent of the Estimator's project factors.</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-6">
            {Object.entries(config.impl_type_multipliers).map(([key, val]) => (
              <NumberInput key={key}
                label={key}
                value={val} onChange={v => updateField(`impl_type_multipliers.${key}`, parseFloat(v.toFixed(2)))}
                suffix="x" min={0.1} max={5} step={0.05}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ── Section 6: Infrastructure ── */}
      <Card data-testid="section-infrastructure">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Server className="w-4 h-4 text-slate-500" />
            <CardTitle className="text-sm font-semibold text-[#0F2040]">Infrastructure Settings</CardTitle>
          </div>
          <p className="text-[11px] text-slate-500 mt-1">Additional effort for specific hosting configurations.</p>
        </CardHeader>
        <CardContent>
          <NumberInput
            label="Private Cloud IF Hours"
            value={config.private_cloud_if_hours} onChange={v => updateField('private_cloud_if_hours', Math.round(v))}
            suffix="hours" min={0} max={200}
            helpText="Infrastructure hours forced into Week 1 for private/on-premise cloud deployments."
          />
        </CardContent>
      </Card>

      {/* ── Section 7: Overlay Phase Allocation % ── */}
      <Card data-testid="section-overlay-phase-fractions">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-violet-500" />
            <CardTitle className="text-sm font-semibold text-[#0F2040]">Overlay Phase Allocation %</CardTitle>
            <Badge className="text-[8px] bg-violet-100 text-violet-700 border-violet-200" variant="outline">Planner Only</Badge>
          </div>
          <p className="text-[11px] text-slate-500 mt-1">Percentage of each overlay component&apos;s total effort allocated to each Implementation Phase. Rows must sum to 100%.</p>
        </CardHeader>
        <CardContent>
          <OverlayPhaseFractionsEditor
            fractions={config.overlay_phase_fractions || {}}
            onChange={(next) => updateField('overlay_phase_fractions', next)}
          />
          {!overlayValid && (
            <div className="flex items-center gap-2 mt-3 text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="overlay-validation-error">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
              <span className="text-[11px]">Row totals must equal 100% for: <strong>{overlayInvalidRows.join(', ')}</strong></span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Section 8: Change History ── */}
      <Card data-testid="section-changelog">
        <CardHeader className="pb-2 cursor-pointer select-none" onClick={() => setChangelogOpen(!changelogOpen)}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <History className="w-4 h-4 text-slate-500" />
              <CardTitle className="text-sm font-semibold text-[#0F2040]">Change History</CardTitle>
              {changelogTotal > 0 && (
                <Badge variant="secondary" className="text-[10px] h-5 px-1.5">{changelogTotal}</Badge>
              )}
            </div>
            {changelogOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
          <p className="text-[11px] text-slate-500 mt-1">Audit trail of all planning configuration changes.</p>
        </CardHeader>
        {changelogOpen && (
          <CardContent className="pt-0">
            {changelog.length === 0 ? (
              <p className="text-xs text-slate-400 py-4 text-center">No changes recorded yet.</p>
            ) : (
              <div className="space-y-3">
                {changelog.map((entry, i) => (
                  <div key={entry.timestamp || `changelog-${i}`} className={`border-l-2 ${entry.is_restore ? 'border-violet-300 bg-violet-50/30' : 'border-slate-200'} pl-3 py-1.5 rounded-r-md`} data-testid={`changelog-entry-${i}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        {entry.is_restore && <Undo2 className="w-3 h-3 text-violet-500" />}
                        <span className="text-xs font-medium text-[#0F2040]">{entry.user_name || entry.user_email}</span>
                        <span className="text-[10px] text-slate-400">
                          {new Date(entry.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          {' '}
                          {new Date(entry.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {entry.is_restore && <Badge variant="outline" className="text-[9px] h-4 px-1 text-violet-600 border-violet-300">Restore</Badge>}
                      </div>
                      {isSuperAdmin && entry.snapshot_before && (
                        <Button
                          variant="ghost" size="sm"
                          className="h-6 px-2 text-[10px] text-slate-400 hover:text-violet-600 hover:bg-violet-50"
                          onClick={(e) => { e.stopPropagation(); setRestoreTarget(entry); }}
                          data-testid={`changelog-restore-${i}`}
                        >
                          <Undo2 className="w-3 h-3 mr-1" /> Restore
                        </Button>
                      )}
                    </div>
                    {entry.summary && entry.is_restore && (
                      <div className="text-[10px] text-violet-600 mb-1">{entry.summary}</div>
                    )}
                    <div className="space-y-0.5">
                      {entry.changes.map((c, j) => {
                        const label = fieldLabels[c.field] || c.field.split('.').map(s => s.replace(/_/g, ' ')).join(' > ');
                        return (
                          <div key={`${entry.timestamp || i}-${c.field}-${j}`} className="text-[11px] text-slate-600 flex items-baseline gap-1.5 flex-wrap">
                            <span className="font-medium text-slate-700">{label}:</span>
                            <span className="text-red-500/70 line-through">{typeof c.old === 'object' ? JSON.stringify(c.old) : String(c.old ?? '—')}</span>
                            <span className="text-slate-400">→</span>
                            <span className="text-emerald-600 font-medium">{typeof c.new === 'object' ? JSON.stringify(c.new) : String(c.new ?? '—')}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {changelogTotal > 10 && (
                  <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                    <span className="text-[10px] text-slate-400">
                      Page {changelogPage} of {Math.ceil(changelogTotal / 10)}
                    </span>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" disabled={changelogPage <= 1} onClick={() => fetchChangelog(changelogPage - 1)}
                        className="h-6 px-2 text-[10px]" data-testid="changelog-prev">Prev</Button>
                      <Button variant="ghost" size="sm" disabled={changelogPage >= Math.ceil(changelogTotal / 10)} onClick={() => fetchChangelog(changelogPage + 1)}
                        className="h-6 px-2 text-[10px]" data-testid="changelog-next">Next</Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* ── Restore Confirmation Dialog ── */}
      <AlertDialog open={!!restoreTarget} onOpenChange={(open) => { if (!open) setRestoreTarget(null); }}>
        <AlertDialogContent data-testid="restore-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-[#0F2040]">
              <Undo2 className="w-5 h-5 text-violet-500" />
              Restore Configuration
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will restore the planning configuration to the state <strong>before</strong> the change made on{' '}
              <strong>
                {restoreTarget && new Date(restoreTarget.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                {' at '}
                {restoreTarget && new Date(restoreTarget.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
              </strong>
              {' by '}
              <strong>{restoreTarget?.user_name || restoreTarget?.user_email}</strong>.
              <br /><br />
              All current settings will be overwritten. This action is logged and can itself be restored later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="restore-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRestore}
              disabled={restoring}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="restore-confirm-btn"
            >
              {restoring ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Undo2 className="w-4 h-4 mr-1" />}
              Restore
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
